import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { CheckCircle2, XCircle, AlertCircle, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

// Map "waiting" status to better label
const getStatusLabel = (status) => {
  if (!status || status === 'waiting') return 'Skipped';
  switch (status) {
    case 'pass': return 'Pass';
    case 'fail': return 'Fail';
    case 'warning': return 'Warning';
    default: return 'Skipped';
  }
};

const getStatusBadge = (status) => {
  if (!status || status === 'waiting') {
    return <Badge className="bg-gray-400 text-white text-xs">Skipped</Badge>;
  }
  switch (status) {
    case 'pass':
      return <Badge className="bg-green-600 text-white text-xs">Pass</Badge>;
    case 'fail':
      return <Badge className="bg-red-600 text-white text-xs">Fail</Badge>;
    case 'warning':
      return <Badge className="bg-yellow-600 text-white text-xs">Warning</Badge>;
    default:
      return <Badge className="bg-gray-400 text-white text-xs">Skipped</Badge>;
  }
};

const getStatusIcon = (status) => {
  if (!status || status === 'waiting') {
    return <Clock className="w-4 h-4 text-gray-400" />;
  }
  switch (status) {
    case 'pass':
      return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    case 'fail':
      return <XCircle className="w-4 h-4 text-red-600" />;
    case 'warning':
      return <AlertCircle className="w-4 h-4 text-yellow-600" />;
    default:
      return <Clock className="w-4 h-4 text-gray-400" />;
  }
};

const getStatusBgColor = (status) => {
  if (!status || status === 'waiting') return 'bg-gray-50 border-gray-200';
  switch (status) {
    case 'pass': return 'bg-green-50 border-green-200';
    case 'fail': return 'bg-red-50 border-red-200';
    case 'warning': return 'bg-yellow-50 border-yellow-200';
    default: return 'bg-gray-50 border-gray-200';
  }
};

// Collapsible Subsection for tests within a category
export const TestSubsection = ({ icon: Icon, iconColor, title, tests, results }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Calculate summary
  const displayTests = results?.length > 0 ? results : tests;
  const passed = results?.filter(t => t.status === 'pass').length || 0;
  const failed = results?.filter(t => t.status === 'fail').length || 0;
  const warnings = results?.filter(t => t.status === 'warning').length || 0;
  const total = displayTests?.length || 0;
  
  // Determine header styling based on results
  const hasFailures = failed > 0;
  const hasWarnings = warnings > 0 && !hasFailures;
  const hasPassed = passed > 0 && !hasFailures && !hasWarnings;
  
  return (
    <div>
      {/* Collapsible Header */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className={`w-full flex items-center justify-between p-2.5 rounded-lg border transition-colors mb-2 ${
          hasFailures ? 'bg-red-50 border-red-200 hover:bg-red-100' :
          hasWarnings ? 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100' :
          hasPassed ? 'bg-green-50 border-green-200 hover:bg-green-100' :
          'bg-gray-50 border-gray-200 hover:bg-gray-100'
        }`}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-5 h-5 ${iconColor}`} />}
          <h3 className="font-semibold text-base">{title}</h3>
          {total > 0 && (
            <span className="text-xs text-gray-500">({total})</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Summary badges */}
          {results?.length > 0 && (
            <div className="flex items-center gap-2 text-xs">
              {passed > 0 && <span className="text-green-600 font-medium flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3" />{passed}</span>}
              {failed > 0 && <span className="text-red-600 font-medium flex items-center gap-0.5"><XCircle className="w-3 h-3" />{failed}</span>}
              {warnings > 0 && <span className="text-yellow-600 font-medium flex items-center gap-0.5"><AlertCircle className="w-3 h-3" />{warnings}</span>}
            </div>
          )}
          {isCollapsed ? (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </button>
      
      {/* Test Items - Collapsible */}
      {!isCollapsed && (
        <div className="space-y-2">
          {displayTests?.map((test, index) => {
            const result = results?.find(r => 
              r.name === test.name || 
              r.name?.toLowerCase().includes(test.name?.toLowerCase().split(' ')[0])
            ) || test;
            const status = result?.status;
            
            return (
              <div
                key={index}
                className={`flex items-center justify-between p-3 rounded-lg border ${getStatusBgColor(status)}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {getStatusIcon(status)}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm">{result?.name || test.name}</p>
                    {result?.message && (
                      <p className="text-xs text-gray-600 mt-1">{result.message}</p>
                    )}
                  </div>
                </div>
                {getStatusBadge(status)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Main section wrapper (for larger categories like Operations, Financials, etc.)
export const TestSection = ({ title, icon: Icon, tests, testDefinitions }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Calculate summary
  const passed = tests.filter(t => t.status === 'pass').length;
  const failed = tests.filter(t => t.status === 'fail').length;
  const warnings = tests.filter(t => t.status === 'warning').length;
  const skipped = tests.filter(t => !t.status || t.status === 'waiting').length;
  const total = tests.length || testDefinitions?.length || 0;
  
  // Determine header color based on failures
  const hasFailures = failed > 0;
  const hasWarnings = warnings > 0 && !hasFailures;
  
  return (
    <div className="space-y-2">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
          hasFailures ? 'bg-red-50 border-red-200 hover:bg-red-100' :
          hasWarnings ? 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100' :
          tests.length > 0 ? 'bg-green-50 border-green-200 hover:bg-green-100' :
          'bg-gray-50 border-gray-200 hover:bg-gray-100'
        }`}
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className={`h-5 w-5 ${
            hasFailures ? 'text-red-600' :
            hasWarnings ? 'text-yellow-600' :
            tests.length > 0 ? 'text-green-600' :
            'text-gray-600'
          }`} />}
          <span className="font-semibold text-base">{title}</span>
          <span className="text-sm text-gray-500">({total} tests)</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            {passed > 0 && <span className="text-green-600 font-medium flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3" /> {passed}</span>}
            {failed > 0 && <span className="text-red-600 font-medium flex items-center gap-0.5"><XCircle className="w-3 h-3" /> {failed}</span>}
            {warnings > 0 && <span className="text-yellow-600 font-medium flex items-center gap-0.5"><AlertCircle className="w-3 h-3" /> {warnings}</span>}
            {skipped > 0 && total > 0 && <span className="text-gray-400 flex items-center gap-0.5"><Clock className="w-3 h-3" /> {skipped}</span>}
          </div>
          {isCollapsed ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>
      
      {/* Test Items - Collapsible */}
      {!isCollapsed && (
        <div className="space-y-2 pl-2">
          {(tests.length > 0 ? tests : testDefinitions || []).map((test, idx) => {
            const result = tests.find(t => t.name?.includes(test.name?.split(' ')[0])) || test;
            const status = result?.status || 'waiting';
            
            return (
              <div
                key={idx}
                className={`flex items-center justify-between p-3 rounded-lg border ${getStatusBgColor(status)}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {getStatusIcon(status)}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{result?.name || test.name}</p>
                    {result?.message && status !== 'waiting' && (
                      <p className="text-xs text-gray-600 mt-0.5 truncate">{result.message}</p>
                    )}
                  </div>
                </div>
                {getStatusBadge(status)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
